// /home — Module Launchpad
//
// Tile grid of visible modules — replaces /dashboard as the post-login
// landing for users who prefer browsing modules over analytics. Stays in
// lock-step with the sidebar via the shared getEligibleTopLevelSidebarItems
// helper and the per-user hiddenIds preference. A module missing from the plan
// keeps its tile, with a lock (docs/plan-limits-plan.md §7).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer, Button, Input } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePlanFeatures } from '@/hooks/use-plan-feature';
import {
  applyPlanLocks,
  getEligibleTopLevelSidebarItems,
  type AdminSidebarItem,
} from '@/router';
import {
  useSidebarPrefsStore,
  NON_HIDEABLE_SIDEBAR_IDS,
} from '@/stores/use-sidebar-prefs-store';
import {
  ModuleIcon,
  moduleGradient,
  moduleTint,
  type ModuleId,
} from '@/constants/module-icons';

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);
const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

// Short helper text per module id. Falls back to the module label if absent.
const TILE_BLURB: Record<string, string> = {
  dashboard: 'KPIs, sales wave, drill-down analytics',
  tenants: 'Tenant onboarding & lifecycle',
  users: 'Staff accounts & roles',
  'platform-users': 'Super-admin / admin / moderator',
  'billing-platform': 'Subscriptions & plans',
  'my-subscription': 'Your plan, invoices, renewals',
  locations: 'Stores, outlets, warehouses',
  products: 'Catalog, variants, barcodes',
  categories: 'Product organization',
  customers: 'CRM, RFM, feedback',
  loyalty: 'Points & rewards program',
  vouchers: 'Coupons & gift cards',
  memberships: 'Subscription tiers for customers',
  communications: 'Campaigns, templates, social',
  commissions: 'Staff payouts & settlements',
  shifts: 'Attendance & cash closing',
  restaurant: 'Tables, recipes, menus',
  festive: 'Seasonal events & bookings',
  'credit-accounts': 'Credit billing & invoices',
  'corporate-accounts': 'B2B customer accounts',
  'exchange-rates': 'Multi-currency rates',
  'staff-performance': 'Sales & shift metrics',
  discounts: 'Discount rules & promos',
  expenses: 'Capture & approve expenses',
  quotations: 'Sales quotes & analytics',
  'sales-orders': 'Deliveries, invoices & payments',
  'sales-invoices': 'Customer invoices & receipts',
  approvals: 'Pending workflow approvals',
  inventory: 'Stock, transfers, valuation',
  reports: 'Sales, GST, P&L, payments',
  notifications: 'Inbox & broadcasts',
  audit: 'Activity log of all changes',
  'sync-health': 'POS offline sync status',
  backups: 'Database backup management',
  'data-export': 'GDPR-compliant CSV/ZIP export',
  'dead-letters': 'Failed message queue',
  'system-health': 'Infra & service status',
  settings: 'Business config & integrations',
};

export function HomePage() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const flags = useFeatureFlag();
  const hasPlanFeature = usePlanFeatures();
  const hiddenIds = useSidebarPrefsStore((s) => s.hiddenIds);
  const recentPaths = useSidebarPrefsStore((s) => s.recentPaths);
  const clearRecents = useSidebarPrefsStore((s) => s.clearRecents);
  const [query, setQuery] = useState('');

  const isPlatformUser = PLATFORM_ROLES.has(profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const eligible: AdminSidebarItem[] = useMemo(
    () =>
      applyPlanLocks(
        getEligibleTopLevelSidebarItems({ isPlatformUser, roleLevel, flags }),
        hasPlanFeature,
      ),
    [isPlatformUser, roleLevel, flags, hasPlanFeature],
  );

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);
  const visibleTiles = useMemo(
    () =>
      eligible
        .filter((it) => it.id !== 'home') // never tile the launchpad itself
        .filter((it) => NON_HIDEABLE_SIDEBAR_IDS.has(it.id) || !hiddenSet.has(it.id)),
    [eligible, hiddenSet],
  );

  const q = query.trim().toLowerCase();
  const filteredTiles = useMemo(() => {
    if (!q) return visibleTiles;
    return visibleTiles.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        (TILE_BLURB[it.id] ?? '').toLowerCase().includes(q),
    );
  }, [visibleTiles, q]);

  const recentTiles = useMemo(() => {
    if (q) return []; // search hides recents to avoid double rendering
    const byPath = new Map(visibleTiles.map((t) => [t.path, t]));
    return recentPaths
      .map((p) => byPath.get(p))
      .filter((t): t is AdminSidebarItem => Boolean(t));
  }, [recentPaths, visibleTiles, q]);

  const firstName = (profile?.name ?? '').split(' ')[0] || 'there';

  return (
    <PageContainer>
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white">Welcome back, {firstName}</h1>
          <p className="mt-1 text-sm text-white/85">
            Jump straight into a module — or open the dashboard for today&apos;s KPIs.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate('/dashboard')}
          className="shrink-0"
        >
          Go to Dashboard →
        </Button>
      </div>

      {/* Search */}
      <div className="mb-5 max-w-md">
        <Input
          placeholder="Search modules…"
          value={query}
          onChange={(value) => setQuery(value)}
        />
      </div>

      {/* Recently used */}
      {recentTiles.length > 0 && (
        <div className="mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wide">
              Recently used
            </h2>
            <button
              type="button"
              onClick={clearRecents}
              className="text-xs text-neutral-500 hover:text-brand-600 transition-colors"
            >
              Clear
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {recentTiles.map((it) => (
              <RecentTile key={it.id} item={it} onClick={() => navigate(it.path)} />
            ))}
          </div>
        </div>
      )}

      {/* All modules */}
      <h2 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 uppercase tracking-wide mb-2">
        {q ? `Results (${filteredTiles.length})` : 'All modules'}
      </h2>

      {filteredTiles.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md border border-neutral-100 dark:border-neutral-700 p-8 text-center animate-fade-in">
          {q ? (
            <>
              <p className="text-neutral-700 dark:text-neutral-200 font-medium">
                No modules match &ldquo;{query}&rdquo;.
              </p>
              <Button variant="secondary" className="mt-3" onClick={() => setQuery('')}>
                Clear search
              </Button>
            </>
          ) : (
            <>
              <p className="text-neutral-700 dark:text-neutral-200 font-medium">
                All optional modules are hidden.
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Re-enable modules in Settings → Sidebar Modules.
              </p>
              <Button
                variant="primary"
                className="mt-3"
                onClick={() => navigate('/settings/sidebar')}
              >
                Open Sidebar Settings
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTiles.map((it) => (
            <ModuleTile key={it.id} item={it} onClick={() => navigate(it.path)} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

// ─── Tile components ──────────────────────────────────────────────────────────

function ModuleTile({
  item,
  onClick,
}: {
  item: AdminSidebarItem;
  onClick: () => void;
}) {
  const blurb = TILE_BLURB[item.id] ?? item.label;
  const gradient = moduleGradient(item.id);
  const tint = moduleTint(item.id);
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'group text-left rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700 hover:border-transparent animate-slide-up flex items-center gap-4 ' +
        tint
      }
    >
      <div
        className={
          'w-16 h-16 rounded-2xl flex items-center justify-center text-white shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200 bg-gradient-to-br shrink-0 ' +
          gradient
        }
      >
        <ModuleIcon
          id={item.id as ModuleId}
          className="h-9 w-9"
          strokeWidth={2}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-lg text-neutral-900 dark:text-white truncate">
          {item.label}
        </p>
        <p className="text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2 mt-0.5">
          {blurb}
        </p>
      </div>
      {item.locked && <LockMark className="h-8 w-8" />}
    </button>
  );
}

function RecentTile({
  item,
  onClick,
}: {
  item: AdminSidebarItem;
  onClick: () => void;
}) {
  const gradient = moduleGradient(item.id);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-2 bg-white dark:bg-neutral-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 px-3 py-2 border border-neutral-100 dark:border-neutral-700 hover:border-brand-200 dark:hover:border-brand-700"
    >
      <div
        className={
          'w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0 bg-gradient-to-br ' +
          gradient
        }
      >
        <ModuleIcon id={item.id as ModuleId} className="h-5 w-5" strokeWidth={2} />
      </div>
      <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate">
        {item.label}
      </span>
      {item.locked && <LockMark className="h-6 w-6" />}
    </button>
  );
}

/** Marks a module that is not in the plan. The tile still opens it — the page explains. */
function LockMark({ className }: { className: string }) {
  return (
    <span
      role="img"
      aria-label="Not in your plan"
      title="Not in your plan"
      className={
        'flex shrink-0 items-center justify-center rounded-full bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300 ' +
        className
      }
    >
      <svg className="h-1/2 w-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </span>
  );
}
